// Template Campaigns API - Campanhas de disparo via templates WhatsApp
// /opt/projects/saas_odonto/frontend/src/services/templateCampaignApi.ts

import api from './api.ts';

// =============================================================================
// TYPES
// =============================================================================

export interface TemplateCampaign {
    id: number;
    clinic_id: number;
    name: string;
    template_name: string;
    base_message?: string;
    target_tag_id: number;
    target_tag_name?: string;
    interval_min_seconds: number;
    interval_max_seconds: number;
    start_hour: string; // "08:00"
    end_hour: string;   // "18:00"
    weekdays: number[]; // [0,1,2,3,4,5,6]
    status: 'draft' | 'running' | 'paused' | 'completed' | 'canceled';
    total_contacts: number;
    sent_count: number;
    failed_count: number;
    response_count: number;
    response_rate: number;
    credits_consumed: number;
    created_at: string;
    updated_at: string;
    launched_at?: string;
    completed_at?: string;
}

export interface TemplateCampaignCreate {
    name: string;
    template_name: string;
    base_message?: string;
    target_tag_id: number;
    interval_min_seconds?: number; // default: 36
    interval_max_seconds?: number; // default: 120
    start_hour: string;
    end_hour: string;
    weekdays?: number[];
}

export interface TemplateCampaignUpdate {
    name?: string;
    template_name?: string;
    base_message?: string;
    target_tag_id?: number;
    interval_min_seconds?: number;
    interval_max_seconds?: number;
    start_hour?: string;
    end_hour?: string;
    weekdays?: number[];
}

export interface ApprovedTemplate {
    id: number;
    name: string;
    category: string;
    status: string;
    use_case: string;
    body_text?: string;
}

export interface AudiencePreview {
    tag_id: number;
    tag_name: string;
    contact_count: number;
}

export interface CampaignStats {
    campaign_id: number;
    total_contacts: number;
    pending: number;
    scheduled: number;
    sent: number;
    failed: number;
    responded: number;
    progress_percent: number;
    response_rate: number;
}

export interface CampaignExecution {
    id: number;
    contact_id: number;
    contact_name?: string;
    phone: string;
    status: 'pending' | 'scheduled' | 'sent' | 'failed';
    scheduled_at?: string;
    sent_at?: string;
    delivered_at?: string;
    read_at?: string;
    responded_at?: string;
    error_message?: string;
}

export interface CampaignExecutionsList {
    campaign_id: number;
    total: number;
    executions: CampaignExecution[];
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Lista templates aprovados com use_case=campaign
 */
export async function getApprovedTemplates(clinicId: number): Promise<ApprovedTemplate[]> {
    try {
        const response = await api.get('/api/template-campaigns/templates', {
            params: { clinic_id: clinicId }
        });
        return response.data;
    } catch (error) {
        console.error('[getApprovedTemplates] Erro:', error);
        throw error;
    }
}

/**
 * Preview de audiência por tag
 */
export async function getAudiencePreview(clinicId: number, tagId: number): Promise<AudiencePreview> {
    try {
        const response = await api.get('/api/template-campaigns/audience-preview', {
            params: { clinic_id: clinicId, tag_id: tagId }
        });
        return response.data;
    } catch (error) {
        console.error('[getAudiencePreview] Erro:', error);
        throw error;
    }
}

/**
 * Lista campanhas da clínica
 */
export async function getTemplateCampaigns(
    clinicId: number,
    status?: string
): Promise<TemplateCampaign[]> {
    try {
        const params: Record<string, any> = { clinic_id: clinicId };
        if (status) params.status = status;

        const response = await api.get('/api/template-campaigns', { params });
        return response.data;
    } catch (error) {
        console.error('[getTemplateCampaigns] Erro:', error);
        throw error;
    }
}

/**
 * Busca campanha por ID
 */
export async function getTemplateCampaign(
    clinicId: number,
    campaignId: number
): Promise<TemplateCampaign> {
    try {
        const response = await api.get(`/api/template-campaigns/${campaignId}`, {
            params: { clinic_id: clinicId }
        });
        return response.data;
    } catch (error) {
        console.error('[getTemplateCampaign] Erro:', error);
        throw error;
    }
}

/**
 * Cria nova campanha
 */
export async function createTemplateCampaign(
    clinicId: number,
    campaign: TemplateCampaignCreate
): Promise<TemplateCampaign> {
    try {
        const response = await api.post('/api/template-campaigns', campaign, {
            params: { clinic_id: clinicId }
        });
        return response.data;
    } catch (error) {
        console.error('[createTemplateCampaign] Erro:', error);
        throw error;
    }
}

/**
 * Atualiza campanha (só draft)
 */
export async function updateTemplateCampaign(
    clinicId: number,
    campaignId: number,
    campaign: TemplateCampaignUpdate
): Promise<TemplateCampaign> {
    try {
        const response = await api.put(`/api/template-campaigns/${campaignId}`, campaign, {
            params: { clinic_id: clinicId }
        });
        return response.data;
    } catch (error) {
        console.error('[updateTemplateCampaign] Erro:', error);
        throw error;
    }
}

/**
 * Deleta campanha (só draft ou completed)
 */
export async function deleteTemplateCampaign(
    clinicId: number,
    campaignId: number
): Promise<void> {
    try {
        await api.delete(`/api/template-campaigns/${campaignId}`, {
            params: { clinic_id: clinicId }
        });
    } catch (error) {
        console.error('[deleteTemplateCampaign] Erro:', error);
        throw error;
    }
}

/**
 * Lança campanha (inicia envios)
 */
export async function launchTemplateCampaign(
    clinicId: number,
    campaignId: number
): Promise<TemplateCampaign> {
    try {
        const response = await api.post(`/api/template-campaigns/${campaignId}/launch`, null, {
            params: { clinic_id: clinicId }
        });
        return response.data;
    } catch (error) {
        console.error('[launchTemplateCampaign] Erro:', error);
        throw error;
    }
}

/**
 * Pausa campanha
 */
export async function pauseTemplateCampaign(
    clinicId: number,
    campaignId: number
): Promise<TemplateCampaign> {
    try {
        const response = await api.post(`/api/template-campaigns/${campaignId}/pause`, null, {
            params: { clinic_id: clinicId }
        });
        return response.data;
    } catch (error) {
        console.error('[pauseTemplateCampaign] Erro:', error);
        throw error;
    }
}

/**
 * Retoma campanha pausada
 */
export async function resumeTemplateCampaign(
    clinicId: number,
    campaignId: number
): Promise<TemplateCampaign> {
    try {
        const response = await api.post(`/api/template-campaigns/${campaignId}/resume`, null, {
            params: { clinic_id: clinicId }
        });
        return response.data;
    } catch (error) {
        console.error('[resumeTemplateCampaign] Erro:', error);
        throw error;
    }
}

/**
 * Estatísticas da campanha
 */
export async function getCampaignStats(
    clinicId: number,
    campaignId: number
): Promise<CampaignStats> {
    try {
        const response = await api.get(`/api/template-campaigns/${campaignId}/stats`, {
            params: { clinic_id: clinicId }
        });
        return response.data;
    } catch (error) {
        console.error('[getCampaignStats] Erro:', error);
        throw error;
    }
}

/**
 * Lista as execuções de uma campanha
 */
export async function getCampaignExecutions(
    clinicId: number,
    campaignId: number,
    limit: number = 50,
    offset: number = 0
): Promise<CampaignExecutionsList> {
    try {
        const response = await api.get(`/api/template-campaigns/${campaignId}/executions`, {
            params: { clinic_id: clinicId, limit, offset }
        });
        return response.data;
    } catch (error) {
        console.error('[getCampaignExecutions] Erro:', error);
        throw error;
    }
}

// Export default object
export default {
    getApprovedTemplates,
    getAudiencePreview,
    getTemplateCampaigns,
    getTemplateCampaign,
    createTemplateCampaign,
    updateTemplateCampaign,
    deleteTemplateCampaign,
    launchTemplateCampaign,
    pauseTemplateCampaign,
    resumeTemplateCampaign,
    getCampaignStats,
    getCampaignExecutions,
};
